import { Quill } from 'react-quill'
import { DeltaOperation } from 'quill'

import { REGEXP } from 'shared/consts/regExp'

import { TDelta, TDeltaOp, IQuillOptions } from 'shared/types'

import { uploadPhoto, uploadBase64 } from 'modulesCommon/graphql/mutations'
import { isImgTooLarge, setImageValidationError } from 'shared/utils'
import { MAX_FILE_SIZE } from 'shared/consts'

export class PasteInterceptor {
  private editor: any
  private options: IQuillOptions

  constructor(quill: typeof Quill, options: IQuillOptions = null) {
    this.editor = quill
    this.options = options

    this.editor.getModule('toolbar').addHandler('image', this.imageHandler)

    this.editor.root.addEventListener('paste', (event: any) => this.onPaste(event))

    this.editor.root.addEventListener('drop', (event: DragEvent) => this.onPaste(event))

    setTimeout(() => {
      this.editor.clipboard.addMatcher(Node.ELEMENT_NODE, this.cleanUpStyles)
    }, 0)
  }

  private imageHandler = () => {
    const input = document.createElement('input')

    input.setAttribute('type', 'file')
    input.click()
    input.onchange = () => {
      const files = input.files || new FileList()

      this.readFiles(files, this.insert)
    }
  }

  private async onPaste(event: any) {
    const isPastedText = !!event?.clipboardData?.getData('text').length

    this.editor.isPasting = true

    const data = event instanceof DragEvent ? event.dataTransfer : event.clipboardData
    const files = data ? Array.from(data.files) : []

    if (files.length && !isPastedText) {
      event.preventDefault()

      if (event.dataTransfer?.files?.length) {
        this.readFiles(event.dataTransfer.files, this.insert)
      }

      if (event.clipboardData?.items?.length) {
        this.readFiles(event.clipboardData.items, this.insert)
      }

      this.editor.setSelection(this.editor.selectionRange)
    }

    setTimeout(async () => {
      const deltaOps = this.editor.getContents().ops
      const deltaImages = deltaOps
        .filter(({ insert }: DeltaOperation) => !!insert?.image && !insert.image.includes('/images/'))
        .map(({ insert }: DeltaOperation) => insert.image.split(',')[1])

      if (deltaImages.length) {
        const result = await Promise.all(deltaImages.map((image: string) => uploadBase64(image)))

        const newDelta = deltaOps.map((item: DeltaOperation) => {
          if (!!item.insert?.image && !item.insert.image.includes('/images/')) {
            const link: any = result.shift()

            item.insert.image = link.Location
          }

          return item
        })

        this.editor.setContents(newDelta, 'user')
      }
    }, 10)

    this.editor.isPasting = false
  }

  private insert = (dataUrl: string) => {
    this.editor.insertEmbed(this.editor.selection.savedRange.index, 'image', dataUrl, 'user')
  }

  private readFiles = (files: FileList, callback: (dataUrl: string) => void) => {
    const filesArray = [...files]

    filesArray.forEach((file: any) => {
      if (!file.type.match(REGEXP.IMG)) return

      const reader = new FileReader()
      const blob = file.getAsFile ? file.getAsFile() : file

      if (isImgTooLarge(blob, MAX_FILE_SIZE)) {
        setImageValidationError()

        return
      }

      uploadPhoto(blob).then((data) => {
        callback(`${data.Location}`)
      })

      if (blob instanceof Blob) reader.readAsDataURL(blob)
    })
  }

  private cleanUpStyles = (node: Node, delta: TDelta) => {
    delta.ops = delta.ops.map((op: TDeltaOp) => {
      delete op.attributes?.align
      delete op.attributes?.background

      if (this.options?.minimal) {
        delete op.attributes?.color
      }

      return op
    })

    return delta
  }
}
